import { Button } from '@shared/components/ui/button';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import { usePermissions } from '@shared/hooks/usePermissions';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProductForm } from '../../hooks/useProductForm';
import { ClientSelector, CreateUpdateButton, ProductTabs } from '../interaction';
import { useAuthStore } from '@shared/stores/authStore';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { ClientsApiAdapter } from '@modules/Clients/List/adapters/ClientsApiAdapter';
import { AdjustStockModal, AdjustStockModalRef } from '../modals/AdjustStockModal';
import { ActivateProductBatchesModal, ActivateProductBatchesModalRef } from '../modals/ActivateProductBatchesModal';
import { CreateLinkModal, CreateLinkModalRef } from '../modals/CreateLinkModal';
import { CreateStockModal, CreateStockModalRef } from '../modals/CreateStockModal';

// Mock de producto para el modal de enlaces
const productoMock = {
  id: 1,
  name: 'Pan keto con agregado de semillas',
  code: 'HGFDERTH',
  src: 'https://img-global.cpcdn.com/recipes/f35be78c16fd987d/1280x1280sq70/photo.webp',
};

export const Main = () => {
  const { t } = useTranslation('createEditProductsModule');
  const { getTeamType } = usePermissions();
  const createStockModalRef = useRef<CreateStockModalRef>(null);
  const adjustStockModalRef = useRef<AdjustStockModalRef>(null);
  const activateBatchesModalRef = useRef<ActivateProductBatchesModalRef>(null);
  const createLinkModalRef = useRef<CreateLinkModalRef>(null);
  const clientConfirmRef = useRef<ConfirmModalRef>(null);
  const linkUnlinkConfirmRef = useRef<ConfirmModalRef>(null);

  const [selectedTenantChildId, setSelectedTenantChildId] = useState<number | null>(null);
  const parentTeamId = useAuthStore((s) => s.team?.id);
  const isTenant3pl = getTeamType() === 'tenant_3pl';

  const clientsHttpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const clientsAdapter = useMemo(() => new ClientsApiAdapter(clientsHttpClient), [clientsHttpClient]);

  // Hook que maneja toda la l?gica del formulario
  const {
    // Estados del formulario
    nombre,
    setNombre,
    descripcion,
    setDescripcion,
    codigo,
    setCodigo,
    coste,
    setCoste,
    categoria,
    setCategoria,
    imagenPreview,
    setImagenPreview,
    imageFile,
    setImageFile,
    length,
    setLength,
    heigth,
    setHeigth,
    width,
    setWidth,
    weight,
    setWeight,
    weightUnit,
    setWeightUnit,
    dimensionUnit,
    setDimensionUnit,
    batchActive,
    setBatchActive,
    batchType,
    setBatchType,

    // Estados de datos externos
    categorias,
    loadingCategorias,
    shops,
    ubicaciones,
    loadingUbicaciones,
    stocks,
    stocksFromApi,
    enlaces,
    initialData,

    // Referencias
    productDataRef,

    // Funciones de manejo
    handleAddStock,
    handleDeleteStock,
    handleAddLink,
    handleDeleteLink,
    filtrarUbicaciones,
    handleCancel,

    // Validaciones
    isSaveDisabled,

    // Utilidades
    productId,
    navigate,
    toast,
    queryClient,
    refetchProduct,
  } = useProductForm();

  // tenant_external solo puede ver la informaci?n del producto en modo edici?n, no editarla
  // En modo creaci?n (sin productId) debe poder crear el producto
  const isReadOnly = getTeamType() === 'tenant_external' && !!productId;

  // Solo aplicar el bloqueo en modo creaci?n y si es 3PL
  const isFormLocked = isTenant3pl && !productId && !selectedTenantChildId;

  // Ref para rastrear si ya se inicializ? el tenantChildId desde initialData
  const hasInitializedTenantChildRef = useRef(false);

  // Extraer el team_id del producto de forma memoizada para evitar re-renders innecesarios
  const productTeamId = useMemo(() => {
    if (!initialData) return null;
    return initialData.team_id || initialData.team?.id || null;
  }, [initialData]);

  // Obtener el team_id del producto cuando se est? editando
  useEffect(() => {
    if (productId && productTeamId && isTenant3pl && !hasInitializedTenantChildRef.current) {
      if (!selectedTenantChildId) {
        setSelectedTenantChildId(Number(productTeamId));
        hasInitializedTenantChildRef.current = true;
      }
    }
    // Resetear el ref cuando cambia el productId
    if (!productId) {
      hasInitializedTenantChildRef.current = false;
    }
  }, [productId, productTeamId, isTenant3pl, selectedTenantChildId]); // Usar productTeamId en lugar de initialData completo

  // Funci?n para resetear el formulario
  const resetForm = useCallback(() => {
    setNombre('');
    setDescripcion('');
    setCodigo('');
    setCoste('');
    setCategoria('');
    setImagenPreview(null);
    setImageFile(null);
    setLength('');
    setHeigth('');
    setWidth('');
    setWeight('');
  }, [
    setNombre,
    setDescripcion,
    setCodigo,
    setCoste,
    setCategoria,
    setImagenPreview,
    setImageFile,
    setLength,
    setHeigth,
    setWidth,
    setWeight,
  ]);

  // Aplicar selecci?n de cliente
  const applyTenantChildSelection = useCallback(
    (childId: number) => {
      setSelectedTenantChildId(childId);
      resetForm();
    },
    [resetForm],
  );

  // Manejar selecci?n de cliente
  const handleTenantChildSelect = useCallback(
    async (value: string) => {
      const parsedValue = Number(value);
      if (!value || Number.isNaN(parsedValue)) {
        return;
      }

      if (!selectedTenantChildId) {
        applyTenantChildSelection(parsedValue);
        return;
      }

      if (selectedTenantChildId === parsedValue) {
        return;
      }

      let childName = '';
      try {
        const client = await clientsAdapter.getClientById(parsedValue);
        childName = client?.name ?? '';
      } catch {
        childName = '';
      }

      clientConfirmRef.current?.open(
        () => applyTenantChildSelection(parsedValue),
        undefined,
        t('clientSelector.confirm.title'),
        t('clientSelector.confirm.message', {
          childName,
        }),
        t('clientSelector.confirm.confirm'),
        t('clientSelector.confirm.cancel'),
      );
    },
    [applyTenantChildSelection, clientsAdapter, selectedTenantChildId, t],
  );

  // Funciones de manejo de modales
  const handleOpenCreateStock = () => {
    createStockModalRef.current?.open(handleAddStock, { isEdit: false });
  };

  const handleOpenCreateLink = () => {
    createLinkModalRef.current?.open(() => {
      // tras crear, volver a cargar producto desde API
      refetchProduct();
    });
  };

  const handleOpenEditStock = (row: { ubicacionId?: string; codigo: string; stock?: number }) => {
    if (!row?.ubicacionId || !productId) return;
    adjustStockModalRef.current?.open(
      {
        productId,
        locationId: row.ubicacionId,
        locationCode: row.codigo,
        currentQty: typeof row.stock === 'number' ? row.stock : undefined,
      },
      () => {},
    );
  };

  const handleOpenActivateBatches = () => {
    if (!productId) return;
    const locations = (isTenant3pl && stocksFromApi.length > 0 ? stocksFromApi : stocks).map((row) => ({
      id: Number(row.ubicacionId),
      code: row.codigo,
      stock: row.stock ?? 0,
    }));

    activateBatchesModalRef.current?.open({
      productId: Number(productId),
      locations,
      onSuccess: refetchProduct,
    });
  };

  // Breadcrumbs para la p?gina de creaci?n/edici?n de producto
  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = [{ label: t('breadcrumbs.products'), path: '/products' }];

    if (productId) {
      // Modo edici?n: mostrar nombre del producto si est? disponible
      const productName = initialData?.name || t('breadcrumbs.edit_product');
      return [...baseBreadcrumbs, { label: productName }];
    } else {
      // Modo creaci?n
      return [...baseBreadcrumbs, { label: t('breadcrumbs.create_product') }];
    }
  }, [t, productId, initialData?.name]);
  usePageNavigation({ breadcrumbs });

  return (
    <>
      {/* Header Section - Secci?n de encabezado con botones de acci?n */}
      <div className="flex items-center flex-wrap gap-3 absolute right-4 top-2">
        <Button
          variant="outline"
          size="lg"
          className="shrink-0"
          data-testid="cancel-product-button"
          onClick={handleCancel}
        >
          {isReadOnly ? t('buttons.back') : t('buttons.cancel')}
        </Button>
        {!isReadOnly && (
          <CreateUpdateButton
            nombre={nombre}
            descripcion={descripcion}
            codigo={codigo}
            coste={coste}
            categoria={categoria}
            imageFile={imageFile}
            length={length}
            heigth={heigth}
            width={width}
            weight={weight}
            enlaces={enlaces}
            stocks={stocks}
            productId={productId}
            queryClient={queryClient}
            navigate={navigate}
            t={(key: string) => t(key)}
            toast={({ title, description, variant }) => toast({ title, description, variant: variant as any })}
            weightUnit={weightUnit}
            dimensionUnit={dimensionUnit}
            batchActive={batchActive}
            batchType={batchType}
            tenantChildId={selectedTenantChildId}
            disabled={isSaveDisabled || isFormLocked}
            initialProductData={productId ? initialData : undefined}
            onEditSuccess={productId ? refetchProduct : undefined}
          />
        )}
      </div>

      {/* Content Section - Secci?n de contenido */}
      <div className="px-2 sm:px-6 pt-4">
        {/* Selector de cliente - Para usuarios 3PL (en creaci?n o edici?n) */}
        {isTenant3pl && (
          <ClientSelector
            parentTeamId={parentTeamId}
            comboboxEnabled={Boolean(isTenant3pl && parentTeamId)}
            selectedTenantChildId={selectedTenantChildId}
            onSelectTenantChild={handleTenantChildSelect}
            isDisabled={!!productId}
            isLocked={isFormLocked}
            mode={productId ? 'edit' : 'create'}
          />
        )}

        {/* Product Tabs Section - Secci?n de pesta?as del producto */}
        {/* Solo mostrar cuando el formulario no est? bloqueado - Only show when form is not locked */}
        {!isFormLocked && (
          <ProductTabs
            // Referencias
            productDataRef={productDataRef}
            // Estados del formulario
            nombre={nombre}
            setNombre={setNombre}
            descripcion={descripcion}
            setDescripcion={setDescripcion}
            codigo={codigo}
            setCodigo={setCodigo}
            coste={coste}
            setCoste={setCoste}
            categoria={categoria}
            setCategoria={setCategoria}
            imagenPreview={imagenPreview}
            setImagenPreview={setImagenPreview}
            imageFile={imageFile}
            setImageFile={setImageFile}
            length={length}
            setLength={setLength}
            heigth={heigth}
            setHeigth={setHeigth}
            width={width}
            setWidth={setWidth}
            weight={weight}
            setWeight={setWeight}
            weightUnit={weightUnit}
            setWeightUnit={setWeightUnit}
            dimensionUnit={dimensionUnit}
            setDimensionUnit={setDimensionUnit}
            batchActive={batchActive}
            setBatchActive={setBatchActive}
            batchType={batchType}
            setBatchType={setBatchType}
            // Estados de datos externos
            categorias={categorias}
            loadingCategorias={loadingCategorias || isFormLocked}
            stocks={isTenant3pl && stocksFromApi.length > 0 ? stocksFromApi : stocks}
            enlaces={enlaces}
            shops={shops}
            initialData={initialData}
            // Funciones de manejo
            onAddStock={handleOpenCreateStock}
            onDeleteStock={handleDeleteStock}
            onEditStock={handleOpenEditStock}
            onCreateLink={handleOpenCreateLink}
            onDeleteLink={handleDeleteLink}
            // Utilidades
            productId={productId}
            readOnly={isReadOnly}
            linkUnlinkConfirmRef={linkUnlinkConfirmRef}
            onOpenActivateBatches={handleOpenActivateBatches}
          />
        )}
      </div>

      {/* Modals Section - Secci?n de modales */}
      <ConfirmModal ref={clientConfirmRef} />
      <ConfirmModal ref={linkUnlinkConfirmRef} />
      <CreateStockModal
        ref={createStockModalRef}
        ubicaciones={ubicaciones}
        filtrarUbicaciones={filtrarUbicaciones}
        stocks={stocks}
        loadingUbicaciones={loadingUbicaciones}
        productId={productId}
      />
      <CreateLinkModal
        ref={createLinkModalRef}
        producto={productoMock}
        onCreateLink={handleAddLink}
        productId={productId}
      />
      <AdjustStockModal ref={adjustStockModalRef} />
      <ActivateProductBatchesModal ref={activateBatchesModalRef} />
    </>
  );
};

export default Main;
